import { motion, AnimatePresence } from 'motion/react'
import type { Pillar } from './data'

interface PillarDotNavProps {
  pillars: Pillar[]
  activeIndex: number
  visible: boolean
  onDotClick: (idx: number) => void
}

/**
 * Fixed dot navigation that appears on the left edge while scrolling through
 * the pillars section. Visibility is controlled by the parent via `visible`.
 */
export function PillarDotNav({
  pillars,
  activeIndex,
  visible,
  onDotClick,
}: PillarDotNavProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.nav
          key="pillar-dot-nav"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          aria-label="Jump to pillar"
          className="flex flex-col gap-3 max-lg:hidden"
          style={{
            position: 'fixed',
            left: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 50,
          }}
        >
          {pillars.map((p, i) => (
            <button
              key={p.label}
              onClick={() => onDotClick(i)}
              aria-label={`Go to ${p.label}`}
              className="group flex items-center gap-2.5 focus:outline-none"
            >
              {/* Dot */}
              <motion.span
                animate={{
                  width: activeIndex === i ? 10 : 6,
                  height: activeIndex === i ? 10 : 6,
                  background:
                    activeIndex === i ? '#FFB606' : 'rgba(255,255,255,0.25)',
                  boxShadow:
                    activeIndex === i
                      ? '0 0 10px 3px rgba(255,182,6,0.6)'
                      : '0 0 0px 0px transparent',
                }}
                transition={{ duration: 0.3 }}
                style={{ display: 'block', borderRadius: 999 }}
              />
              {/* Label — slides in when active */}
              <motion.span
                animate={{
                  opacity: activeIndex === i ? 1 : 0,
                  maxWidth: activeIndex === i ? 140 : 0,
                }}
                transition={{ duration: 0.25 }}
                className="text-xs font-semibold uppercase tracking-wider overflow-hidden whitespace-nowrap pointer-events-none"
                style={{ color: '#FFB606' }}
              >
                {p.label}
              </motion.span>
            </button>
          ))}
        </motion.nav>
      )}
    </AnimatePresence>
  )
}
